import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import {
  DamagePinFormValues,
  DamageStatus,
} from "services/api/fleet/vehicles/damages/types";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
} from "services/forms/hooks";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { Button } from "ui/components/Button/Button";
import Modal from "ui/components/Modal/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { DAMAGE_PIN_EDIT_FORM_SCHEMA } from "../validation";
import {
  DAMAGE_STATUS_HEX,
  DEFAULT_DAMAGE_STATUS_HEX,
} from "./consts";

interface DamagePinEditFormValues {
  name: string;
  description: string;
  vehicleDamageStatusId: string;
}

const initialFormValues: DamagePinEditFormValues = {
  name: "",
  description: "",
  vehicleDamageStatusId: "",
};

interface DamagePinEditModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (pin: DamagePinFormValues) => void;
  pin: DamagePinFormValues | null;
  statuses: DamageStatus[];
}

const DamagePinEditModal: React.FC<DamagePinEditModalProps> = ({
  show,
  onHide,
  onSave,
  pin,
  statuses,
}) => {
  const { t } = useTranslate();
  const [formValues, setFormValues] =
    useState<DamagePinEditFormValues>(initialFormValues);
  const [errors, setErrors] = useState<Errors>({});

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  useEffect(() => {
    if (show && pin) {
      setFormValues({
        name: pin.name ?? "",
        description: pin.description ?? "",
        vehicleDamageStatusId: pin.vehicleDamageStatusId ?? "",
      });
      setErrors({});
    }
  }, [show, pin]);

  // Boja statusa ide uz stavku — ista ona kojom se pin ucrtava na fotografiju.
  const statusItems: DropdownOption[] = statuses.map((s) => ({
    value: s.id,
    text: s.name,
    color:
      DAMAGE_STATUS_HEX[s.color ?? ""] ?? DEFAULT_DAMAGE_STATUS_HEX,
  }));

  const handleSave = () => {
    const isValid = validateYup(
      formValues,
      DAMAGE_PIN_EDIT_FORM_SCHEMA,
      setErrors
    );
    if (!isValid) return;
    if (!pin) return;

    onSave({
      ...pin,
      name: (formValues.name ?? "").trim(),
      description: (formValues.description ?? "").trim(),
      vehicleDamageStatusId: formValues.vehicleDamageStatusId,
    });
  };

  return (
    <Modal show={show} position="centered" size="small" onHide={onHide}>
      {/* Zaglavlje ide kao dijete modala — `header` prop Modal ne renderira. */}
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={pin?.id ? "Ažuriranje oznake štete" : "Nova oznaka štete"}
        onHide={onHide}
      />
      <ModalBody fixedFooterSpacing={false}>
        <div className="p-4 flex flex-col ">
          <div className="mb-3">
            <Dropdown
              name="vehicleDamageStatusId"
              label="Status"
              placeholder="Odaberi status"
              options={statusItems}
              onChange={handleDropdownChange}
              error={errors.vehicleDamageStatusId}
              required
              value={formValues.vehicleDamageStatusId}
            />
          </div>
          <div className="mb-3">
            <TextField
              type="text"
              name="name"
              label="Naziv"
              placeholder="Unesite naziv"
              onChange={handleTextFieldChange}
              error={errors.name}
              value={formValues.name}
              required
            />
          </div>
          <div className="mb-3">
            <TextArea
              name="description"
              label="Opis"
              placeholder="Unesite opis"
              onChange={handleTextFieldChange}
              value={formValues.description}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 flex justify-end gap-3">
          <Button variant="outlined" onClick={onHide}>
            Odustani
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Spremi
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(DamagePinEditModal);
